import { createContext, useContext } from "react";
import { MixpanelEventName } from "@portal/app/_common/types/mixpanel";
import type { RequestOptions } from "mixpanel-browser";
import type { SavedExperimentAttribution } from "../utils/savedFormFunnelAnswers";
import type { FlowStepDescriptor } from "@portal/app/_common/lib/flowStepTracking";
import type { FormFunnelEverflowAffiliateParams } from "@portal/app/_common/lib/formFunnelEverflowTracking";
import type { HostedFlowTrackingPhase, HostedFlowTrackingStep } from "../utils/flowStepDescriptors";

export interface TrackingIds extends FormFunnelEverflowAffiliateParams<string | null> {
  experimentAttributionToken?: string;
  // Core IDs
  customerId: string | null;
  marketplaceEventId: string | null;

  // Compliance IDs
  jornayaLeadId: string | null;
  trustedFormCertId: string | null;
  everflowTransactionId: string | null;

  // Facebook cookies
  facebookClickId: string | null;
  facebookBrowserId: string | null;

  googleClickId?: string;
  googleWbraid?: string;
  googleGbraid?: string;
  googleAdsCapturedAt?: string;

  // Google Analytics
  gaClientId: string | null;

  // Everflow affiliate params
  everflowUid: string | null;
  everflowSourceId: string | null;
}

export interface FormFunnelTrackingContextType {
  savedExperimentAttribution?: SavedExperimentAttribution;
  experimentViewKey?: string;
  trackFlowStep: (descriptor: FlowStepDescriptor) => void;
  trackHostedFlowStep: (step: HostedFlowTrackingStep, phase?: HostedFlowTrackingPhase) => void;
  trackStepComplete: (stepName: string, stepIndex: number) => void;
  trackFormSubmit: () => void;
  getTrackingIds: () => Promise<TrackingIds>;
  trackEvent: (
    eventName: MixpanelEventName,
    properties?: Record<string, unknown>,
    options?: RequestOptions
  ) => void;
}

export const FormFunnelTrackingContext = createContext<FormFunnelTrackingContextType | undefined>(
  undefined
);

export const useFormFunnelTracking = () => {
  const context = useContext(FormFunnelTrackingContext);
  if (!context) {
    throw new Error("useFormFunnelTracking must be used within a FormFunnelTrackingProvider");
  }
  return context;
};
